import React from 'react';
import './ReadinessAnalytics.css';

/**
 * Pure maths for the readiness card, exported for tests. `levels`: [{ key, label, tone, count }], `total`: students in
 * the college, `assessed`: students with evidence. Nothing here is estimated: every figure is a ratio of the counts
 * the dashboard API already returned.
 */
export function readinessStats(levels, total, assessed) {
  const t = Math.max(0, Number(total) || 0);
  const pct = (n) => (t > 0 ? Math.round((n / t) * 1000) / 10 : 0);
  const count = (k) => levels.find(l => l.key === k)?.count || 0;
  const ready = count('placement_ready') + count('top_performer');
  const a = Math.max(0, Number(assessed) || 0);
  return {
    segments: levels.map(l => ({ ...l, pct: pct(l.count) })),
    readyCount: ready,
    readyPct: pct(ready),
    // "of those we can measure" — the fairer headline when coverage is low
    readyOfAssessedPct: a > 0 ? Math.round((ready / a) * 1000) / 10 : null,
    coveragePct: pct(a),
  };
}

const ReadinessAnalytics = ({ levels, total, assessed, onSelectUnassessed }) => {
  const s = readinessStats(levels, total, assessed);
  if (!total) return <div className="ra-empty">No students yet — add students to see readiness.</div>;
  return (
    <div className="ra">
      <div className="ra-head">
        <div className="ra-headline">
          <span className="ra-big">{s.readyPct}%</span>
          <span className="ra-cap">of {total} students are placement-ready or strong</span>
          {s.readyOfAssessedPct != null && assessed < total && (
            <span className="ra-sub">{s.readyOfAssessedPct}% of the {assessed} assessed</span>
          )}
        </div>
        <div className="ra-coverage" aria-label={`Assessment coverage ${s.coveragePct}%`}>
          <div className="ra-coverage-top"><span>Assessed</span><b>{assessed}/{total}</b></div>
          <div className="ra-track"><div className="ra-fill" style={{ width: `${s.coveragePct}%` }} /></div>
          {assessed < total && (
            onSelectUnassessed
              ? <button type="button" className="ra-link" onClick={onSelectUnassessed}>{total - assessed} still to assess</button>
              : <span className="ra-sub">{total - assessed} still to assess</span>
          )}
        </div>
      </div>

      <div className="ra-stack" role="img" aria-label={s.segments.map(x => `${x.label} ${x.count}`).join(', ')}>
        {s.segments.filter(x => x.count > 0).map(x => (
          <span key={x.key} className={`ra-seg ra-seg--${x.tone}`} style={{ width: `${x.pct}%` }} title={`${x.label}: ${x.count} (${x.pct}%)`} />
        ))}
      </div>

      <ul className="ra-legend">
        {s.segments.map(x => (
          <li key={x.key}>
            <i className={`ra-dot ra-seg--${x.tone}`} />
            <span className="ra-legend-label">{x.label}</span>
            <b>{x.count}</b>
            <em>{x.pct}%</em>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ReadinessAnalytics;
